import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Logo } from "../Logo";
import { Footer } from "../Footer";

// Turn the exact phrases "Privacy Policy" / "Terms of Service" into in-app links.
const LINKS = [
  { phrase: "Privacy Policy", to: "/privacy" },
  { phrase: "Terms of Service", to: "/terms" },
];

const linkify = (text) => {
  const parts = [];
  let rest = text;
  let key = 0;
  while (rest.length) {
    let hit = null;
    for (const l of LINKS) {
      const idx = rest.indexOf(l.phrase);
      if (idx !== -1 && (hit === null || idx < hit.idx)) hit = { ...l, idx };
    }
    if (!hit) {
      parts.push(rest);
      break;
    }
    if (hit.idx > 0) parts.push(rest.slice(0, hit.idx));
    parts.push(
      <Link key={`lk-${key++}`} to={hit.to} className="text-teal underline underline-offset-2 hover:text-teal-dark">
        {hit.phrase}
      </Link>
    );
    rest = rest.slice(hit.idx + hit.phrase.length);
  }
  return parts;
};

const LegalHeader = () => (
  <header className="fixed top-0 left-0 right-0 z-50 bg-navy/80 backdrop-blur-xl border-b border-white/[0.06]">
    <div className="mx-auto max-w-[1240px] px-6 md:px-12 h-[72px] flex items-center justify-between">
      <Link to="/" className="flex items-center transition-opacity hover:opacity-80" aria-label="SalesMoat home">
        <Logo className="h-8 md:h-9 w-auto" />
      </Link>
      <Link to="/" className="group inline-flex items-center gap-1.5 text-sm font-medium text-slate-300 hover:text-white transition-colors">
        <ArrowLeft className="h-4 w-4 text-teal transition-transform duration-300 group-hover:-translate-x-0.5" />
        Back to home
      </Link>
    </div>
  </header>
);

export const LegalPage = ({ doc }) => (
  <div className="min-h-screen flex flex-col" data-testid="legal-page">
    <LegalHeader />
    <main className="flex-1 pt-32 pb-20">
      <article className="mx-auto max-w-3xl px-6 md:px-8">
        <h1 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight text-slate-50 leading-[1.1]">
          {doc.title}
        </h1>
        <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-sm text-slate-500">
          <span>Effective Date: {doc.effective}</span>
          {doc.updated && <span>Last Updated: {doc.updated}</span>}
        </div>

        {doc.intro?.map((p, i) => (
          <p key={`in-${i}`} className="mt-6 text-[15px] leading-relaxed text-slate-400">
            {linkify(p)}
          </p>
        ))}

        {doc.sections.map((s, i) => (
          <section key={`sec-${i}`} className="mt-11">
            {s.heading && (
              <h2 className="font-heading text-xl md:text-2xl font-medium text-slate-100 mb-4">
                <span className="text-teal mr-2">{i + 1}.</span>
                {s.heading}
              </h2>
            )}
            {s.paras?.map((p, j) => (
              <p key={`p-${i}-${j}`} className="mb-4 text-[15px] leading-relaxed text-slate-400">
                {linkify(p)}
              </p>
            ))}
            {s.bullets && (
              <ul className="mb-4 space-y-2.5">
                {s.bullets.map((b, k) => (
                  <li key={`b-${i}-${k}`} className="flex gap-3 text-[15px] leading-relaxed text-slate-400">
                    <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-teal/70" />
                    <span>
                      {typeof b === "string" ? (
                        linkify(b)
                      ) : (
                        <>
                          <span className="font-medium text-slate-200">{b.label}:</span> {b.text}
                        </>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {s.contact && (
              <div className="mt-4 rounded-xl border border-white/[0.07] bg-navy-surface px-5 py-4 text-[15px] leading-relaxed">
                <a href="mailto:info@salesmoat.ai" className="text-teal hover:text-teal-dark">
                  {s.contact[0]}
                </a>
                {s.contact.slice(1).map((line, m) => (
                  <p key={`c-${i}-${m}`} className="text-slate-400">
                    {line}
                  </p>
                ))}
              </div>
            )}
          </section>
        ))}
      </article>
    </main>
    <Footer />
  </div>
);
